import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import {
  Calendar, Clock, ChevronLeft, ChevronRight, Save, Loader2,
  Check, X, RefreshCw, User, MapPin
} from "lucide-react";

// Day names
const DAY_NAMES = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const DAY_NAMES_SHORT = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

// Time slots from 6 AM to 10 PM
const TIME_SLOTS = [];
for (let hour = 6; hour <= 22; hour++) {
  TIME_SLOTS.push(`${String(hour).padStart(2, '0')}:00`);
}

// Format time for display
const formatTime = (timeString) => {
  if (!timeString) return "";
  const [hours, minutes] = timeString.split(':');
  const hour = parseInt(hours, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 || 12;
  return `${hour12}${minutes !== '00' ? ':' + minutes : ''} ${ampm}`;
};

// Format date
const formatDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString + "T00:00:00");
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

// Check if a time slot falls within a schedule or appointment
const isTimeInRange = (time, startTime, endTime) => {
  if (!startTime || !endTime) return false;
  return time >= startTime && time < endTime;
};

// Stats Card Component
const StatCard = ({ label, value, icon: Icon, highlight }) => (
  <Card className={`border-slate-200 ${highlight ? "bg-[#d4a853]/10 border-[#d4a853]" : ""}`}>
    <CardContent className="p-4">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
          highlight ? "bg-[#d4a853]/20 text-[#d4a853]" : "bg-slate-100 text-slate-500"
        }`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <p className="text-2xl font-bold text-[#1e3a5f]">{value}</p>
          <p className="text-xs text-slate-500">{label}</p>
        </div>
      </div>
    </CardContent>
  </Card>
);

// Schedule Form Modal
const ScheduleFormModal = ({ schedule, onSave, onClose }) => {
  const [selectedDays, setSelectedDays] = useState([0, 1, 2, 3, 4]); // Mon-Fri by default
  const [startTime, setStartTime] = useState("09:00");
  const [endTime, setEndTime] = useState("17:00");
  const [saving, setSaving] = useState(false);

  // Initialize from existing schedule
  useEffect(() => {
    if (schedule && schedule.length > 0) {
      const days = [...new Set(schedule.map(s => s.day_of_week))];
      setSelectedDays(days);
      
      // Get most common start/end times
      const starts = schedule.map(s => s.start_time);
      const ends = schedule.map(s => s.end_time);
      if (starts.length > 0) setStartTime(starts[0]);
      if (ends.length > 0) setEndTime(ends[0]);
    }
  }, [schedule]);

  const toggleDay = (day) => {
    if (selectedDays.includes(day)) {
      setSelectedDays(selectedDays.filter(d => d !== day));
    } else {
      setSelectedDays([...selectedDays, day].sort());
    }
  };

  const applyWeekdays = () => {
    setSelectedDays([0, 1, 2, 3, 4]); // Mon-Fri
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (selectedDays.length === 0) {
      toast.error("Select at least one day");
      return;
    }
    
    if (startTime >= endTime) {
      toast.error("End time must be after start time");
      return;
    }
    
    setSaving(true);
    try {
      const slots = selectedDays.map(day => ({
        day_of_week: day,
        start_time: startTime,
        end_time: endTime,
        is_available: true
      }));
      
      await onSave(slots);
      onClose();
    } catch (error) {
      // Error handled in parent
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="schedule-form-modal">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="p-4 border-b border-slate-100 flex items-center justify-between">
          <h2 className="text-lg font-bold text-[#1e3a5f]">Set Weekly Schedule</h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Day Selection */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-slate-700">Working Days</label>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={applyWeekdays}
                className="text-xs text-[#d4a853]"
              >
                Apply Weekdays
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {DAY_NAMES_SHORT.map((day, index) => (
                <button
                  key={day}
                  type="button"
                  onClick={() => toggleDay(index)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    selectedDays.includes(index)
                      ? "bg-[#1e3a5f] text-white"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
                  data-testid={`day-${day.toLowerCase()}`}
                >
                  {day}
                </button>
              ))}
            </div>
          </div>
          
          {/* Time Range */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Start Time</label>
              <Input
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className="border-slate-200"
                data-testid="schedule-start-time"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">End Time</label>
              <Input
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                className="border-slate-200"
                data-testid="schedule-end-time"
              />
            </div>
          </div>
          
          {/* Preview */}
          <div className="bg-slate-50 rounded-lg p-3">
            <p className="text-sm text-slate-600">
              <strong>Preview:</strong> {selectedDays.length > 0 
                ? `${selectedDays.map(d => DAY_NAMES_SHORT[d]).join(", ")} from ${formatTime(startTime)} to ${formatTime(endTime)}`
                : "No days selected"}
            </p>
          </div>
          
          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="flex-1 bg-[#1e3a5f] hover:bg-[#152a45]"
              data-testid="schedule-save-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Save Schedule
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Week Grid Component (Desktop)
const WeekGrid = ({ weekData, schedule, onToggleSlot }) => {
  const dates = Object.keys(weekData).sort();
  
  return (
    <div className="overflow-x-auto">
      <div className="min-w-[800px]">
        {/* Header */}
        <div className="grid grid-cols-8 border-b border-slate-200">
          <div className="p-2 text-center text-xs font-medium text-slate-500">Time</div>
          {dates.map(date => {
            const dayData = weekData[date];
            return (
              <div 
                key={date} 
                className={`p-2 text-center border-l border-slate-200 ${
                  dayData.is_today ? "bg-[#d4a853]/10" : ""
                }`}
              >
                <p className={`text-sm font-semibold ${dayData.is_today ? "text-[#d4a853]" : "text-[#1e3a5f]"}`}>
                  {DAY_NAMES_SHORT[dayData.day_of_week]}
                </p>
                <p className="text-xs text-slate-500">{formatDate(date)}</p>
                {dayData.is_today && (
                  <span className="inline-block mt-1 text-[10px] bg-[#d4a853] text-white px-1.5 py-0.5 rounded">
                    Today
                  </span>
                )}
              </div>
            );
          })}
        </div>
        
        {/* Time Slots */}
        {TIME_SLOTS.map(time => (
          <div key={time} className="grid grid-cols-8 border-b border-slate-100">
            <div className="p-2 text-xs text-slate-500 text-center border-r border-slate-100">
              {formatTime(time)}
            </div>
            {dates.map(date => {
              const dayData = weekData[date];
              const daySchedule = dayData.schedule || [];
              const dayAppointments = dayData.appointments || [];
              
              // Check if this time slot is scheduled as available
              const isAvailable = daySchedule.some(s => 
                s.is_available && isTimeInRange(time, s.start_time, s.end_time)
              );
              
              // Check if this time slot has an appointment
              const appointment = dayAppointments.find(a => 
                isTimeInRange(time, a.start_time, a.end_time || `${parseInt(a.start_time.split(':')[0]) + 1}:00`)
              );
              
              let bgColor = "bg-slate-50"; // Off/unavailable
              let textColor = "text-slate-400";
              let content = null;
              
              if (appointment) {
                bgColor = "bg-blue-100";
                textColor = "text-blue-700";
                // Only show appointment info on start time
                if (time === appointment.start_time) {
                  content = (
                    <div className="text-[10px] truncate px-1">
                      <p className="font-medium">{appointment.customer_name}</p>
                    </div>
                  );
                }
              } else if (isAvailable) {
                bgColor = "bg-emerald-50";
                textColor = "text-emerald-600";
              }
              
              return (
                <div
                  key={`${date}-${time}`}
                  className={`p-1 h-10 border-l border-slate-100 ${bgColor} ${textColor} transition-colors cursor-pointer hover:opacity-80`}
                  onClick={() => !appointment && onToggleSlot(dayData.day_of_week, time)}
                  data-testid={`slot-${date}-${time}`}
                >
                  {content}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

// Day View Component (Mobile)
const DayView = ({ date, dayData, schedule, onToggleSlot }) => {
  const daySchedule = dayData?.schedule || [];
  const dayAppointments = dayData?.appointments || [];
  
  return (
    <div className="space-y-1">
      {TIME_SLOTS.map(time => {
        const isAvailable = daySchedule.some(s => 
          s.is_available && isTimeInRange(time, s.start_time, s.end_time)
        );
        
        const appointment = dayAppointments.find(a => 
          isTimeInRange(time, a.start_time, a.end_time || `${parseInt(a.start_time.split(':')[0]) + 1}:00`)
        );
        
        let bgColor = "bg-slate-50";
        let borderColor = "border-slate-200";
        
        if (appointment) {
          bgColor = "bg-blue-50";
          borderColor = "border-blue-200";
        } else if (isAvailable) {
          bgColor = "bg-emerald-50";
          borderColor = "border-emerald-200";
        }
        
        return (
          <div
            key={time}
            className={`flex items-center gap-3 p-3 rounded-lg border ${bgColor} ${borderColor}`}
            onClick={() => !appointment && onToggleSlot(dayData.day_of_week, time)}
          >
            <span className="text-sm font-medium text-slate-600 w-16">{formatTime(time)}</span>
            
            {appointment ? (
              <div className="flex-1">
                <p className="text-sm font-medium text-blue-700">{appointment.customer_name}</p>
                <p className="text-xs text-blue-500">{appointment.service_description}</p>
              </div>
            ) : isAvailable ? (
              <span className="text-sm text-emerald-600">Available</span>
            ) : (
              <span className="text-sm text-slate-400">Off</span>
            )}
            
            {appointment && (
              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">Booked</span>
            )}
          </div>
        );
      })}
    </div>
  );
};

// Empty State
const EmptyState = ({ onSetSchedule }) => (
  <div className="text-center py-16">
    <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
      <Calendar className="w-8 h-8 text-slate-400" />
    </div>
    <h3 className="text-lg font-semibold text-[#1e3a5f] mb-2">No schedule set</h3>
    <p className="text-slate-500 mb-4 max-w-sm mx-auto">
      Set your weekly schedule to let customers know when you're available.
    </p>
    <Button onClick={onSetSchedule} className="bg-[#d4a853] hover:bg-[#b8922d] text-[#1e3a5f]">
      <Clock className="w-4 h-4 mr-2" />
      Set Weekly Schedule
    </Button>
  </div>
);

// Legend Component
const Legend = () => (
  <div className="flex flex-wrap items-center gap-4 text-xs">
    <div className="flex items-center gap-1.5">
      <div className="w-4 h-4 rounded bg-emerald-50 border border-emerald-200" />
      <span className="text-slate-600">Available</span>
    </div>
    <div className="flex items-center gap-1.5">
      <div className="w-4 h-4 rounded bg-blue-100 border border-blue-200" />
      <span className="text-slate-600">Booked</span>
    </div>
    <div className="flex items-center gap-1.5">
      <div className="w-4 h-4 rounded bg-slate-50 border border-slate-200" />
      <span className="text-slate-600">Off</span>
    </div>
  </div>
);

// Main Component
export default function ProSchedulePage() {
  const { token } = useAuth();
  const [schedule, setSchedule] = useState([]);
  const [weekData, setWeekData] = useState({});
  const [stats, setStats] = useState({ scheduled_hours: 0, appointments_this_week: 0, available_hours: 0 });
  const [loading, setLoading] = useState(true);
  const [weekStart, setWeekStart] = useState(null);
  const [showFormModal, setShowFormModal] = useState(false);
  const [mobileDay, setMobileDay] = useState(0); // For mobile day navigation

  // Get current week's Monday
  const getWeekStart = (date = new Date()) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust for Sunday
    return new Date(d.setDate(diff));
  };

  // Fetch schedule
  const fetchSchedule = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await axios.get(`${API}/pro/schedule`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSchedule(response.data.schedule || []);
    } catch (error) {
      toast.error("Failed to load schedule");
    }
  }, [token]);

  // Fetch week data
  const fetchWeekData = useCallback(async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      const dateParam = weekStart ? weekStart.toISOString().split('T')[0] : "";
      const url = dateParam ? `${API}/pro/schedule/week?date=${dateParam}` : `${API}/pro/schedule/week`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setWeekData(response.data.days || {});
      setStats(response.data.stats || {});
      
      if (!weekStart) {
        setWeekStart(new Date(response.data.week_start));
      }
    } catch (error) {
      toast.error("Failed to load week data");
    } finally {
      setLoading(false);
    }
  }, [token, weekStart]);

  useEffect(() => {
    fetchSchedule();
  }, [fetchSchedule]);

  useEffect(() => {
    fetchWeekData();
  }, [fetchWeekData]);

  // Save schedule
  const handleSaveSchedule = async (slots) => {
    try {
      await axios.post(`${API}/pro/schedule`, { slots }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Schedule saved");
      fetchSchedule();
      fetchWeekData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save schedule");
      throw error;
    }
  };

  // Toggle slot (simplified - just opens the form)
  const handleToggleSlot = (dayOfWeek, time) => {
    setShowFormModal(true);
  };

  // Week navigation
  const goToPrevWeek = () => {
    const newStart = new Date(weekStart);
    newStart.setDate(newStart.getDate() - 7);
    setWeekStart(newStart);
  };

  const goToNextWeek = () => {
    const newStart = new Date(weekStart);
    newStart.setDate(newStart.getDate() + 7);
    setWeekStart(newStart);
  };

  const goToThisWeek = () => {
    setWeekStart(getWeekStart());
  };

  // Mobile day navigation
  const dates = Object.keys(weekData).sort();
  const currentMobileDate = dates[mobileDay];
  const currentMobileDayData = weekData[currentMobileDate];

  // Format week range
  const weekRange = weekStart 
    ? `${formatDate(weekStart.toISOString().split('T')[0])} - ${formatDate(new Date(weekStart.getTime() + 6 * 24 * 60 * 60 * 1000).toISOString().split('T')[0])}`
    : "";

  return (
    <div className="space-y-6" data-testid="pro-schedule-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1e3a5f]">Agent Schedule</h1>
          <p className="text-slate-500">Manage your weekly availability</p>
        </div>
        
        <Button
          onClick={() => setShowFormModal(true)}
          className="bg-[#d4a853] hover:bg-[#b8922d] text-[#1e3a5f]"
          data-testid="set-schedule-btn"
        >
          <Clock className="w-4 h-4 mr-2" />
          Set Schedule
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <StatCard 
          label="Hours This Week" 
          value={stats.scheduled_hours || 0} 
          icon={Clock}
        />
        <StatCard 
          label="Appointments" 
          value={stats.appointments_this_week || 0} 
          icon={User}
          highlight={stats.appointments_this_week > 0}
        />
        <StatCard 
          label="Booked Hours" 
          value={stats.booked_hours || 0} 
          icon={Calendar}
        />
        <StatCard 
          label="Available Hours" 
          value={stats.available_hours || 0} 
          icon={Check}
        />
      </div>

      {/* Week Navigation */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <Button variant="ghost" size="sm" onClick={goToPrevWeek}>
              <ChevronLeft className="w-5 h-5" />
            </Button>
            
            <div className="text-center">
              <p className="font-semibold text-[#1e3a5f]">{weekRange}</p>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={goToThisWeek}
                className="text-xs text-[#d4a853]"
              >
                <RefreshCw className="w-3 h-3 mr-1" />
                This Week
              </Button>
            </div>
            
            <Button variant="ghost" size="sm" onClick={goToNextWeek}>
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
          
          {/* Legend */}
          <div className="mt-4 pt-4 border-t border-slate-100">
            <Legend />
          </div>
        </CardContent>
      </Card>

      {/* Schedule Grid */}
      {loading ? (
        <div className="text-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-[#d4a853] mx-auto" />
        </div>
      ) : schedule.length === 0 && Object.keys(weekData).length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="p-0">
            <EmptyState onSetSchedule={() => setShowFormModal(true)} />
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Desktop Grid */}
          <Card className="border-slate-200 hidden md:block">
            <CardContent className="p-4">
              <WeekGrid 
                weekData={weekData} 
                schedule={schedule}
                onToggleSlot={handleToggleSlot}
              />
            </CardContent>
          </Card>
          
          {/* Mobile Day View */}
          <div className="md:hidden">
            {/* Mobile Day Navigation */}
            <Card className="border-slate-200 mb-4">
              <CardContent className="p-2">
                <div className="flex items-center justify-between">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => setMobileDay(Math.max(0, mobileDay - 1))}
                    disabled={mobileDay === 0}
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </Button>
                  
                  <div className="text-center">
                    <p className={`font-semibold ${currentMobileDayData?.is_today ? "text-[#d4a853]" : "text-[#1e3a5f]"}`}>
                      {currentMobileDayData ? DAY_NAMES[currentMobileDayData.day_of_week] : ""}
                    </p>
                    <p className="text-sm text-slate-500">{formatDate(currentMobileDate)}</p>
                    {currentMobileDayData?.is_today && (
                      <span className="inline-block text-[10px] bg-[#d4a853] text-white px-1.5 py-0.5 rounded">
                        Today
                      </span>
                    )}
                  </div>
                  
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => setMobileDay(Math.min(6, mobileDay + 1))}
                    disabled={mobileDay === 6}
                  >
                    <ChevronRight className="w-5 h-5" />
                  </Button>
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-slate-200">
              <CardContent className="p-4">
                {currentMobileDayData ? (
                  <DayView 
                    date={currentMobileDate}
                    dayData={currentMobileDayData}
                    schedule={schedule}
                    onToggleSlot={handleToggleSlot}
                  />
                ) : (
                  <p className="text-center text-slate-500 py-8">No schedule data</p>
                )}
              </CardContent>
            </Card>
          </div>
        </>
      )}

      {/* Schedule Form Modal */}
      {showFormModal && (
        <ScheduleFormModal
          schedule={schedule}
          onSave={handleSaveSchedule}
          onClose={() => setShowFormModal(false)}
        />
      )}
    </div>
  );
}
